import { Component, OnInit, computed, effect, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { ImageTrackerDistributionSettings } from 'dashboard/interfaces/visuals/list-widget.interface';
import { camelCase } from 'lodash-es';
import { BaseModal } from 'shared/abstract/base-modal';
import { VisualsDashboardDistributionSettingsPickerComponent } from '../visuals-dashboard-distribution-settings-picker/visuals-dashboard-distribution-settings-picker.component';
import { VisualsDashboardPeriodPickerComponent } from '../visuals-dashboard-period-picker/visuals-dashboard-period-picker.component';
import { VisualsDashboardSizePickerComponent } from '../visuals-dashboard-size-picker/visuals-dashboard-size-picker.component';

@Component({
    selector: 'app-visual-dashboard-item-edit-modal',
    imports: [
        TranslateModule,
        VisualsDashboardSizePickerComponent,
        VisualsDashboardPeriodPickerComponent,
        VisualsDashboardDistributionSettingsPickerComponent,
    ],
    templateUrl: './visual-dashboard-item-edit-modal.component.html',
    styleUrl: './visual-dashboard-item-edit-modal.component.scss',
})
export class VisualDashboardItemEditModalComponent extends BaseModal implements OnInit {
    data!: any;
    widget = signal<DashboardWidget | null>(null);

    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;

    public closeModal() {
        this.closeModalInstance(undefined);
    }

    selectedSize = signal<VisualsDashboardWidgetSizingEnum | null>(null);
    selectedDistributorSettings = signal<ImageTrackerDistributionSettings | null>(null);

    initialSelectedSize = computed(() => {
        return this.widget()?.selectedSize || null;
    });

    defaultSize = computed(() => {
        const sizes = this.widget()?.availableSizes || [];
        return sizes.find((size) => size.isDefault)?.size || sizes[0]?.size;
    });

    currentSelectedSize = computed(() => {
        return this.selectedSize() ?? this.initialSelectedSize() ?? this.defaultSize();
    });

    hasMultipleSizes = computed(() => {
        return (this.widget()?.availableSizes.length || 0) > 1;
    });

    camelCaseKey = computed(() => {
        return camelCase(this.widget()?.key);
    });

    widgetTitle = computed(() => {
        return `dashboard.visuals.widgets.${this.camelCaseKey()}.title`;
    });

    widgetDescription = computed(() => {
        return this.widget()?.translatedFullDescription || '';
    });

    showDistributionSettings = computed(() => {
        const widget = this.widget();
        if (!widget) {
            return false;
        }
        return widget.imageTrackerDistributionSettings !== null;
    });

    widgetSelectedOptionValue = computed(() => {
        const widget = this.widget();
        if (!widget) {
            return null;
        }
        return widget.selectedPeriodOptionValue;
    });

    showPeriodPicker = computed(() => {
        return this.widgetSelectedOptionValue() !== null;
    });

    selectedPeriodValue = signal<number | null>(null);

    onSizeSelected(size: VisualsDashboardWidgetSizingEnum): void {
        this.selectedSize.set(size);
    }

    onDistributorSettingsChange(settings: ImageTrackerDistributionSettings): void {
        this.selectedDistributorSettings.set(settings);
    }

    onPeriodChange(periodValue: number | null): void {
        this.selectedPeriodValue.set(periodValue);
    }

    constructor() {
        super();
        effect(() => {
            const selectedOptionValue = this.widgetSelectedOptionValue();
            if (selectedOptionValue !== null) {
                this.selectedPeriodValue.set(selectedOptionValue);
            }
        });
    }

    ngOnInit(): void {
        this.widget.set(this.data.item);
    }

    saveChanges() {
        const currentSelectedSize = this.currentSelectedSize();
        const initialSelectedSize = this.widget()?.selectedSize;

        const currentSelectedPeriod = this.selectedPeriodValue();
        const initialSelectedPeriod = this.widget()?.selectedPeriod;

        const distributorSettings = this.selectedDistributorSettings();
        const currentSettings = this.widget()?.imageTrackerDistributionSettings;

        const sizeChanged = currentSelectedSize !== initialSelectedSize;
        const periodChanged = currentSelectedPeriod !== initialSelectedPeriod;
        const distributorSettingsChanged = JSON.stringify(distributorSettings) !== JSON.stringify(currentSettings);

        if (!sizeChanged && !periodChanged && !distributorSettingsChanged) {
            this.closeModalInstance(null);
            return;
        }

        this.closeModalInstance({
            selectedSize: currentSelectedSize,
            selectedPeriod: currentSelectedPeriod,
            selectedDistributorSettings: distributorSettings,
        });
    }
}
